import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { useAuth } from '../contexts/AuthContext'
import { useDocument, useCollection } from '../hooks/useFirestore'
import { getShipTypeLabel, getCrewRoleLabel, CREW_ROLES } from '../lib/shipTypes'
import './ShipDetailPage.css'

const PLACEHOLDER_SHIP = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect fill="#1E1A22" width="100" height="100"/>
    <path d="M20 60 L50 45 L80 60 L75 70 L25 70 Z" fill="#6E2EBF"/>
    <rect x="48" y="30" width="4" height="20" fill="#6E2EBF"/>
    <path d="M52 30 L52 45 L70 40 Z" fill="#8B5CF6"/>
  </svg>
`)

const PLACEHOLDER_AVATAR = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect fill="#1E1A22" width="100" height="100"/>
    <circle cx="50" cy="35" r="20" fill="#6E2EBF"/>
    <ellipse cx="50" cy="85" rx="30" ry="25" fill="#6E2EBF"/>
  </svg>
`)

// Sanitize and render HTML content safely using DOMPurify
function SanitizedHtml({ html }) {
  const sanitized = useMemo(() => {
    if (!html) return ''
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'blockquote'],
      ALLOWED_ATTR: ['href', 'target', 'rel']
    })
  }, [html])

  if (!sanitized) return null
  // Content is sanitized via DOMPurify above - safe to render
  return <div dangerouslySetInnerHTML={{ __html: sanitized }} />
}

export default function ShipDetailPage() {
  const { id } = useParams()
  const { isOwner } = useAuth()
  const { data: ship, loading, error } = useDocument('ships', id)
  const { data: characters } = useCollection('characters')

  if (loading) {
    return (
      <div className="container">
        <div className="ship-detail-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  if (error || !ship) {
    return (
      <div className="container">
        <div className="ship-detail-error">
          <h2>Ship not found</h2>
          <Link to="/ships" className="btn-secondary">Back to Ships</Link>
        </div>
      </div>
    )
  }

  // Hide ship from non-owners if hidden flag is set
  if (ship.hidden === true && !isOwner) {
    return (
      <div className="container">
        <div className="ship-detail-error">
          <h2>Ship not found</h2>
          <Link to="/ships" className="btn-secondary">Back to Ships</Link>
        </div>
      </div>
    )
  }

  // Get character details for crew members
  const crewWithDetails = (ship.crew || []).map(member => {
    const character = characters.find(c => c.id === member.characterId)
    return {
      ...member,
      character
    }
  }).filter(m => m.character) // Only show crew with valid characters

  // Group crew by role importance (order defined in CREW_ROLES)
  const roleOrder = CREW_ROLES.map(r => r.id)
  const sortedCrew = [...crewWithDetails].sort((a, b) => {
    return roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role)
  })

  const isDestroyed = ship.destroyed === true

  return (
    <div className="container">
      <Link to="/ships" className="back-link">← Back to Ships</Link>

      <div className={`ship-detail ${isDestroyed ? 'destroyed' : ''}`}>
        <aside className="ship-sidebar">
          <div className="ship-image-container">
            <img
              src={ship.imageUrl || PLACEHOLDER_SHIP}
              alt={ship.name}
              onError={(e) => { e.target.src = PLACEHOLDER_SHIP }}
            />
            {isDestroyed && (
              <span className="ship-destroyed-badge">Destroyed</span>
            )}
            <span className="ship-type-badge">
              {getShipTypeLabel(ship.type)}
            </span>
          </div>

          {ship.arsenal && (
            <div className="ship-arsenal">
              <h3>Arsenal</h3>
              <p>{ship.arsenal}</p>
            </div>
          )}
        </aside>

        <main className="ship-main">
          <header className="ship-header">
            <h1>{ship.name}</h1>
            <p className="ship-type">{getShipTypeLabel(ship.type)}</p>
          </header>

          {ship.description && (
            <section className="ship-description">
              <h2>About</h2>
              <div className="description-content">
                <SanitizedHtml html={ship.description} />
              </div>
            </section>
          )}

          {sortedCrew.length > 0 && (
            <section className="ship-crew">
              <h2>Crew & Associations</h2>
              <div className="crew-grid">
                {sortedCrew.map((member, index) => (
                  <Link
                    key={index}
                    to={`/characters/${member.character.id}`}
                    className="crew-member-card"
                  >
                    <div className="crew-member-avatar">
                      <img
                        src={member.character.profileImageUrl || PLACEHOLDER_AVATAR}
                        alt={member.character.name}
                        onError={(e) => { e.target.src = PLACEHOLDER_AVATAR }}
                      />
                    </div>
                    <div className="crew-member-info">
                      <span className="crew-member-role">{getCrewRoleLabel(member.role)}</span>
                      <h4>{member.character.name}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {!ship.description && sortedCrew.length === 0 && (
            <div className="ship-empty">
              <p>No details yet for this ship.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
